import React, {
    createContext,
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from "react";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";
import { BrowserRouter, useLocation } from "react-router-dom";
import clsx from "clsx";
import reportWebVitals from "./reportWebVitals";

import Routes, { routesFlat, isProductPage } from "@routes";
import { ScrollToTop, getArticles } from "@helpers";
import { newsFilterOptions } from "@helpers/constants";
import {
    Footer,
    NavDesktop,
    NavMobile,
    CookiesPopup,
    AnnouncementMarquee,
} from "./components";
import HeaderOffsetContext from "./contexts/HeaderOffsetContext";

import { Auth0Provider } from "@auth0/auth0-react";

import "./index.css";
import "react-loading-skeleton/dist/skeleton.css";

ReactGA.initialize("G-KTSEKJ0CT0");

export const CMSContext = createContext(null);
export const NewsContext = createContext(null);

const root = ReactDOM.createRoot(document.getElementById("root"));

const onRedirectCallback = (appState) => {
    window.history.replaceState(
        {},
        document.title,
        appState?.returnTo || window.location.pathname,
    );
};
console.log(window.location.origin);
const providerConfig = {
    domain: process.env.REACT_APP_DOMAIN,
    clientId: process.env.REACT_APP_CLIENT_ID,
    onRedirectCallback,
    authorizationParams: {
        redirect_uri: `${window.location.origin}/callback`,
        ...(process.env.REACT_APP_AUDIENCE
            ? { audience: process.env.REACT_APP_AUDIENCE }
            : null),
    },
};

root.render(
    <Auth0Provider {...providerConfig}>
        <BrowserRouter>
            <Root />
        </BrowserRouter>
    </Auth0Provider>,
);

function Root() {
    const [state, setState] = useState(undefined);

    return (
        <CMSContext.Provider value={{ state, setState }}>
            <Site />
        </CMSContext.Provider>
    );
}

function Site() {
    const location = useLocation();
    const [articles, setArticles] = useState([]);
    const [newsFilter, setNewsFilter] = useState({
        product: newsFilterOptions.product.none,
        type: newsFilterOptions.type.none,
    });
    const marqueeRef = useRef(null);
    const [marqueeHeight, setMarqueeHeight] = useState(0);
    const [desktopNavHeight, setDesktopNavHeight] = useState(0);
    const [mobileNavHeight, setMobileNavHeight] = useState(0);

    useEffect(() => {
        getArticles().then((newsfeed) => {
            setArticles(newsfeed);
        });
    }, []);

    const not403 = location.pathname !== routesFlat.noAccess;
    const productPage = isProductPage();
    const showAnnouncement = !productPage;
    const displayNav = !productPage;
    const displayFooter = displayNav && not403;

    useEffect(() => {
        if (!showAnnouncement) {
            setMarqueeHeight(0);
            return;
        }

        const updateHeight = () => {
            if (marqueeRef.current) {
                setMarqueeHeight(marqueeRef.current.offsetHeight || 0);
            }
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [showAnnouncement]);

    const navGapDesktop = displayNav ? (marqueeHeight > 0 ? 0 : 40) : 0;
    const navGapMobile = displayNav ? (marqueeHeight > 0 ? 0 : 24) : 0;

    const handleDesktopNavHeight = useCallback((height = 0) => {
        setDesktopNavHeight(height);
    }, []);

    const handleMobileNavHeight = useCallback((height = 0) => {
        setMobileNavHeight(height);
    }, []);

    useEffect(() => {
        if (!displayNav) {
            setMobileNavHeight(0);
        }
    }, [displayNav]);

    const headerOffsets = useMemo(() => {
        const desktopTopOffset = marqueeHeight + navGapDesktop;
        const desktopTotalHeight = desktopTopOffset + desktopNavHeight;
        const mobileTopOffset = marqueeHeight + navGapMobile;
        const mobileTotalHeight = mobileTopOffset + mobileNavHeight;

        return {
            marqueeHeight,
            desktop: {
                gap: navGapDesktop,
                topOffset: desktopTopOffset,
                navHeight: desktopNavHeight,
                totalHeight: desktopTotalHeight,
            },
            mobile: {
                gap: navGapMobile,
                topOffset: mobileTopOffset,
                navHeight: mobileNavHeight,
                totalHeight: mobileTotalHeight,
            },
        };
    }, [
        marqueeHeight,
        navGapDesktop,
        navGapMobile,
        desktopNavHeight,
        mobileNavHeight,
    ]);

    const headerCssVars = useMemo(
        () => ({
            "--header-top-desktop": `${headerOffsets.desktop.topOffset}px`,
            "--header-total-desktop": `${headerOffsets.desktop.totalHeight}px`,
            "--header-top-mobile": `${headerOffsets.mobile.topOffset}px`,
            "--header-total-mobile": `${headerOffsets.mobile.totalHeight}px`,
        }),
        [headerOffsets],
    );

    return (
        <div className={siteBodyStyle} style={headerCssVars}>
            {not403 && (
                <>
                    <CookiesPopup />
                    {showAnnouncement && (
                        <AnnouncementMarquee 
                            ref={marqueeRef}
                            text="Jurisdictional MRV Data updated for 2025."
                            label="Latest"
                            dataLink={routesFlat.jmrv}
                            dataLabel="View Data"
                        />
                    )}
                    <NavDesktop
                        topOffset={marqueeHeight}
                        gapOffset={navGapDesktop}
                        onHeightChange={handleDesktopNavHeight}
                    />
                    {displayNav && (
                        <NavMobile
                            topOffset={marqueeHeight}
                            gapOffset={navGapMobile}
                            onHeightChange={handleMobileNavHeight}
                        />
                    )}
                    <ScrollToTop />
                </>
            )}
            <HeaderOffsetContext.Provider value={headerOffsets}>
                <NewsContext.Provider
                    value={{ articles, newsFilter, setNewsFilter }}
                >
                    <Routes />
                </NewsContext.Provider>
            </HeaderOffsetContext.Provider>
            {displayFooter && <Footer />}
        </div>
    );
}

const siteBodyStyle = clsx("p-0 w-full h-full font-body bg-background");

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
